import React, { ReactNode, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { EditItemProps, useEditItem2 } from '../../toolympus/api/useNewItem';
import { Buttons, FormGrid, MoreActionsMenuButton, OccupyFreeSpace, SimpleDialog, TabPanel, TabsHeader, useSaveable, useTabsState } from '../../toolympus/components/primitives';
import { AutoSuggest, AutoSuggestMultiple } from '../../toolympus/components/primitives/AutoSuggests';
import { FormControlsForFields } from '../../toolympus/components/schemed';
import { Schema } from '../../toolympus/hooks/useSchema';
import { ProjectStagesTable } from './ProjectStages';
import { Project } from './types';
import { CuratorsSuggestionsKey, MembersSuggestionsKey } from './useProjectsList';
import { Button, Drawer, IconButton, Typography } from '@mui/material';
import { Close, DeleteOutlined } from '@mui/icons-material';
import { ProjectStateDisplay } from './ProjectStateDisplay';
import { ProjectStatusesTable } from './ProjectStatuses';
import { FieldLabel, FieldValue, ProjectStatusField } from './ProjectsList';
import { useStatusDates } from '../Settings/useStatusDates';
import { FormattedMessage } from 'react-intl';
import { DeadlineNote } from './DeadlineDisplay';
import { useUserSettings } from '../../toolympus/components/Settings/UserSettingsContext';
import { EditingSettings } from '../Settings/useAppSettings';
import { FormSectionPane } from '../../toolympus/components/primitives/PaneContainer';



interface ProjectProps {
  data: EditItemProps<Project>;
  remove: (p: Project) => void;
  projectSchema: Schema;
  stageSchema: Schema;
  statusSchema: Schema;
  
  startEditingStatusCurrent: (p: Project) => void;
  startEditingStatusPrev: (p: Project) => void;
  title?: ReactNode;
  mode: "project" | "process";
}


const useProjectEditing = (project: Project | undefined | null) => {
  const userSettings = useUserSettings();

  useEffect(() => {
    userSettings.ensureLoaded();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const settings = (userSettings.global || {}) as EditingSettings;
  const supportMarkers = useMemo<Set<string>>(
    () => new Set(settings.editing_support || []),
    [settings.editing_support]
  );

  return {
    isEditingRequired: !!settings.editing_support?.length
      && (project?.support || []).some((item) => supportMarkers.has(item)),
  }
}

export const ProjectPanel = (props: ProjectProps) => {
  const { data } = props;

  useSaveable({
    hasChanges: !!data.hasChanges,
    save: () => {
      if(data.dialog.isOpen) {
        data.save()
      }
    },
  });

  const tabs = useTabsState([
    ["stages", "Промежуточные дедлайны"],
    ["statuses", "История задач"],
  ], "stages")

  const statusDates = useStatusDates(props.mode);

  const addMemberManual = useEditItem2<{ member: string }>({
    save: async (item) => {
      const member = item.member.trim();
      if(member && !data.item?.members?.includes(member)) {

      }
      data.update({ members: [...(data.item?.members || []), member ] });
      return item;
    }
  });

  const editing = useProjectEditing(data.item);

  return (
    <Drawer
      anchor="right"
      open={data.dialog.isOpen}
      onClose={() => data.dialog.close()}
      ModalProps={{ keepMounted: false }} // Better performance on mobile
      sx={{
        '& .MuiDrawer-paper': {
          padding: "12px 24px 24px",
          width: { xs: '100%', sm: '80%', md: '70%' }, // Responsive width
          height: '100vh', // Full viewport height
        },
      }}>
      {!!data.item &&
        <Insides>
          <Buttons className="header">
            <Typography variant="h6">{props.title}</Typography>

            <ProjectStateDisplay
              value={data.item.state}
              update={(v) => data.update({ state: v as any })}
              />

            <OccupyFreeSpace />

            <MoreActionsMenuButton
              actions={[
                ["addmembermanual","Добавить участника",() => addMemberManual.startEditing({ member: "" })],
                ["delete",<FormattedMessage id="common.delete" />,() => data.item && props.remove(data.item)],
              ]}
              />

            <IconButton size="small" onClick={() => data.item && props.remove(data.item)}>
              <DeleteOutlined />
            </IconButton>
          </Buttons>

          <FormGrid columns="1fr 150px">
            <FormControlsForFields
              data={data.item}
              onChange={(o,c) => data.update(c)}
              schema={props.projectSchema}
              fields={[
                ["title"],
                ["code"],
              ]}
              />
          </FormGrid>

          <FormGrid columns="1fr 1fr 1fr 1fr">
            
            <Buttons alignItems="flex-end">
              <FormControlsForFields
                data={data.item}
                onChange={(o,c) => data.update(c)}
                schema={props.projectSchema}
                fields={[
                  ["deadline"],
                ]}
                />
              <DeadlineNote
                value={data.item.deadline}
                noRedZone={data.item.state === "completed"}
                />
            </Buttons>

            <FormControlsForFields
              data={data.item}
              onChange={(o,c) => data.update(c)}
              schema={props.projectSchema}
              fields={[
                ["curator", { control: AutoSuggest, suggestionsKey: CuratorsSuggestionsKey }],
                ["heroes", { control: AutoSuggestMultiple, suggestionsKey: MembersSuggestionsKey }],
                ["support", { autoComplete: true }],
              ]}
              />
          </FormGrid>

          <FormGrid columns="1fr 1fr" forceEvenColumns alignItems="start">
            <FormControlsForFields
              data={data.item}
              onChange={(o,c) => data.update(c)}
              schema={props.projectSchema}
              fields={[
                ["members", { control: AutoSuggestMultiple, suggestionsKey: MembersSuggestionsKey }],
                ["experts", { control: AutoSuggestMultiple, suggestionsKey: MembersSuggestionsKey }],
              ]}
              />
          </FormGrid>

          {editing.isEditingRequired &&
            <FormSectionPane title="Редактура">
              <FormGrid columns="1fr 1fr" forceEvenColumns noMargin>
                <FormControlsForFields
                  data={data.item}
                  onChange={(o,c) => data.update(c)}
                  schema={props.projectSchema}
                  fields={[
                    ["editing_size", { label: "Размер, стр"}],
                    ["editing_complexity", { label: "Сложность", autoComplete: true }],
                  ]}
                  />
              </FormGrid>
            </FormSectionPane>}

          <FormGrid columns="1fr 1fr" forceEvenColumns>
            <ProjectStatusField
              alert={data.item.project_status?.alert}
              onClick={e => {
                e.stopPropagation();
                if(data.item) {
                  props.startEditingStatusCurrent(data.item);
                }
              }}>
              <FieldLabel underscore>Задачи {statusDates.dates.current.label}</FieldLabel>
              <FieldValue className="description">
                {data.item.project_status?.description || '-'}
              </FieldValue>
            </ProjectStatusField>
            
            <ProjectStatusField
              alert={data.item.project_status_prev?.alert}
              onClick={e => {
                e.stopPropagation();
                if(data.item) {
                  props.startEditingStatusPrev(data.item);
                }
              }}>
              <FieldLabel>Задачи {statusDates.dates.prev.label}</FieldLabel>
              <FieldValue className="description">
                {data.item.project_status_prev?.description || '-'}
              </FieldValue>
            </ProjectStatusField>

            
          </FormGrid>


          <TabsHeader state={tabs} />

          <TabPanel state={tabs.forTab("stages")}>
            <ProjectStagesTable
              projectId={data.item._id}
              schema={props.stageSchema}
              />

          </TabPanel>

          <TabPanel state={tabs.forTab("statuses")}>
            <ProjectStatusesTable
              projectId={data.item._id}
              schema={props.statusSchema}
              mode={props.mode}
              />

          </TabPanel>



          <OccupyFreeSpace />

          <Buttons className="footer">
            <OccupyFreeSpace />
            <IconButton size="small" onClick={() => data.cancel()}><Close /></IconButton>
            {!!data.hasChanges && <Button color="primary" variant="contained" onClick={() => data.save()}>сохранить</Button>}
          </Buttons>
        </Insides>
        }

        <SimpleDialog
          {...addMemberManual.dialog}
          dialogTitle="Добавить участника"
          noFullscreen
          save={() => addMemberManual.save()}>
          {!!addMemberManual.item && <FormGrid columns="1fr">
            <FormControlsForFields
              data={addMemberManual.item}
              onChange={(o,c) => addMemberManual.update(c)}
              schema={{ member: { label: "Участник" }}}
              fields={[
                ["member", { controlProps: { autoFocus: true }}],
              ]}
              />

          </FormGrid>}
        </SimpleDialog>
    </Drawer>
  )
}

const Insides = styled.div`
  display: flex;
  flex-flow: column;

  height: 100%;

  & .actions {
    justify-content: flex-end;
  }
`;
